// hooks/useSlotPortal.js
import { useEffect, useState, useCallback } from 'react';

export function useSlotPortal(slotName, targetSelector = 'z-unified-nav-v2') {
  const [targetElement, setTargetElement] = useState(null);

  const findElementWithSlot = useCallback(
    (rootElement) => {
      if (!rootElement) return null;

      const shadowRoot = rootElement.shadowRoot;
      if (!shadowRoot) return null;

      const slotElement = shadowRoot.querySelector(`slot[name="${slotName}"]`);
      if (slotElement) {
        return rootElement;
      }

      const elementsWithShadow = Array.from(
        shadowRoot.querySelectorAll('*'),
      ).filter((el) => (el as Element).shadowRoot);

      for (const el of elementsWithShadow) {
        const found = findElementWithSlot(el);
        if (found) return found;
      }

      return null;
    },
    [slotName],
  );

  const findTargetElement = useCallback(() => {
    const navRef = document.querySelector(targetSelector);
    if (!navRef) {
      return null;
    }

    const element = findElementWithSlot(navRef);
    if (element) {
      return element;
    }

    return null;
  }, [findElementWithSlot, slotName, targetSelector]);

  const searchWithRetry = useCallback(() => {
    const element = findTargetElement();
    if (element) {
      setTargetElement(element);
      return;
    }

    // If not found immediately, try a few more times with delays
    let attempts = 0;
    const maxAttempts = 3;

    const retry = () => {
      attempts++;

      const element = findTargetElement();
      if (element) {
        setTargetElement(element);
      } else if (attempts < maxAttempts) {
        setTimeout(retry, 200 * attempts);
      }
    };

    setTimeout(retry, 100);
  }, [findTargetElement, slotName]);

  useEffect(() => {
    searchWithRetry();

    const handleAppChanged = () => {
      // Reset and search again
      setTargetElement(null);
      setTimeout(searchWithRetry, 50);
    };

    window.addEventListener('app-changed', handleAppChanged);

    return () => {
      window.removeEventListener('app-changed', handleAppChanged);
    };
  }, [searchWithRetry, slotName]);

  return targetElement;
}
